import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import useAuth from '../../hooks/use_auth'
import { useSnackbar } from '../../hooks/use_snackbar'

export const WIDGET_IDS = ['details', 'issues', 'progress', 'data']

export const WIDGET_LABELS = {
  details: 'Details',
  issues: 'Issues summary',
  progress: 'Progress',
  data: 'Data',
}

export const WIDGET_DESCRIPTIONS = {
  details: 'Airline, cycle status, and dates for the cycle you are viewing.',
  issues: 'Open metadata, image, and content issue counts.',
  progress: 'Completion progress across cycle content types.',
  data: 'Total data size by asset status.',
}

const readOrder = (preferences) => {
  const widgets = preferences?.widgets
  if (!Array.isArray(widgets)) return [...WIDGET_IDS]
  return widgets.filter((id) => WIDGET_IDS.includes(id))
}

const useDashboardLayout = () => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { addErrorMessage } = useSnackbar()
  const savePreferences = useMutation((dashboard_preferences) =>
    axios.put(`users/${user.id}`, { dashboard_preferences })
  )
  const [draftOrder, setDraftOrder] = useState(null)
  const [dragId, setDragId] = useState(null)
  const editing = draftOrder !== null
  const order = readOrder(user?.dashboard_preferences)
  const displayOrder = editing ? draftOrder : order

  const startEditing = () => {
    setDraftOrder(order)
  }

  const finishEditing = () => {
    if (!draftOrder) return
    const dashboard_preferences = { ...user.dashboard_preferences, widgets: draftOrder }
    queryClient.setQueryData(['/user'], (current) => ({ ...current, dashboard_preferences }))
    savePreferences.mutate(dashboard_preferences, {
      onError: () => addErrorMessage('Could not save your dashboard layout.'),
      onSettled: () => queryClient.invalidateQueries(['/user']),
    })
    setDraftOrder(null)
    setDragId(null)
  }

  const removeWidget = (id) => {
    if (!editing) return
    setDraftOrder((current) => current.filter((widgetId) => widgetId !== id))
  }

  const setWidgetVisible = (id, visible) => {
    if (!editing) return
    setDraftOrder((current) => {
      const isVisible = current.includes(id)
      if (visible && !isVisible) return [...current, id]
      if (!visible && isVisible) return current.filter((widgetId) => widgetId !== id)
      return current
    })
  }

  const onDragStart = (id) => (event) => {
    if (!editing) return
    if (event.target.closest('[data-widget-remove]')) {
      event.preventDefault()
      return
    }
    setDragId(id)
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', id)
  }

  const onDragOver = (event) => {
    if (!editing) return
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }

  const onDrop = (targetId) => (event) => {
    if (!editing) return
    event.preventDefault()
    const sourceId = dragId || event.dataTransfer.getData('text/plain')
    setDragId(null)
    if (!sourceId || sourceId === targetId) return
    setDraftOrder((current) => {
      const next = current.filter((id) => id !== sourceId)
      const targetIndex = next.indexOf(targetId)
      if (targetIndex === -1) return current
      next.splice(targetIndex, 0, sourceId)
      return next
    })
  }

  const onDragEnd = () => {
    setDragId(null)
  }

  return {
    displayOrder,
    editing,
    dragId,
    startEditing,
    finishEditing,
    removeWidget,
    setWidgetVisible,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
  }
}

export default useDashboardLayout
